import type { ReactNode } from 'react';
import type { Manifest } from '../content/data';
import type { Route } from '../navigation/routes';
import { routeHref } from '../navigation/routes';
import type { Progress } from '../progress/progress';
import { allDayIds, completedDayCount, firstIncompleteDayId } from '../progress/progress';

const mainNavigation: Array<{ label: string; route: Route; activePages: Route['page'][] }> = [
  { label: 'Today', route: { page: 'today' }, activePages: ['today', 'day'] },
  { label: 'Roadmap', route: { page: 'roadmap' }, activePages: ['roadmap', 'week'] },
  { label: 'Projects', route: { page: 'projects' }, activePages: ['projects', 'project'] },
  { label: 'Interview', route: { page: 'interview' }, activePages: ['interview'] },
  { label: 'Guides', route: { page: 'guides' }, activePages: ['guides', 'guide', 'fixture'] },
  { label: 'Summary', route: { page: 'summary' }, activePages: ['summary'] }
];

function currentWeekLabel(manifest: Manifest, progress: Progress): string {
  const dayId = firstIncompleteDayId(manifest, progress);
  if (!dayId) return 'Sequence complete';
  const week = manifest.weeks.find((candidate) => dayId.startsWith(candidate.id));
  return week ? `Week ${week.number}` : 'Current week';
}

export function AppShell({
  manifest,
  progress,
  route,
  children
}: {
  manifest: Manifest;
  progress: Progress;
  route: Route;
  children: ReactNode;
}) {
  const completed = completedDayCount(manifest, progress);
  const total = allDayIds(manifest).length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  const weekLabel = currentWeekLabel(manifest, progress);

  return (
    <div className="app-frame">
      <a className="skip-link" href="#main-content">Skip to main content</a>
      {!manifest.completeCurriculum ? (
        <div className="sample-banner">Review build: {manifest.totals.weeks} weeks of content</div>
      ) : null}
      <header className="app-header">
        <a className="brand" href="#/today" aria-label="Frontend Fieldbook home">
          <span>Frontend</span>
          Fieldbook
        </a>

        <nav className="main-nav" aria-label="Primary navigation">
          {mainNavigation.map((item) => {
            const active = item.activePages.includes(route.page);
            return (
              <a href={routeHref(item.route)} aria-current={active ? 'page' : undefined} key={item.label}>
                {item.label}
              </a>
            );
          })}
        </nav>

        <div className="header-progress">
          <div>
            <span>{weekLabel}</span>
            <strong>{completed} of {total} days</strong>
          </div>
          <div className="progress-track" aria-label={`${percent}% complete`} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
            <span style={{ width: `${percent}%` }} />
          </div>
        </div>

        <div className="mobile-progress" aria-label={`${weekLabel}, ${completed} of ${total} days complete`}>
          <span>{weekLabel}</span>
          <strong>{completed} / {total}</strong>
        </div>

        <a
          className="settings-link"
          href="#/settings"
          aria-current={route.page === 'settings' ? 'page' : undefined}
        >
          Settings
        </a>
      </header>
      {children}
    </div>
  );
}
